import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {useReservation} from 'context/reservation';
import {useFetchCheckoutSettings} from './useFetchCheckoutSettings';
import {useSummary} from 'context/summary';
import {OFFER_STATUSES, ORIGINS} from '@chekin/core';

const AVAILABLE_ORIGINS = [ORIGINS.guesty2, ORIGINS.oracle, ORIGINS.mews2];

dayjs.extend(isSameOrBefore);

function getIsCheckoutDate(currentCheckoutDate?: string, checkoutTime?: string): boolean {
  if (!currentCheckoutDate || !checkoutTime) {
    return false;
  }
  const currentDate = dayjs();
  const checkoutDate = dayjs(currentCheckoutDate);
  const currentTime = currentDate.format('HH:mm');

  return checkoutDate.isSameOrBefore(currentDate, 'day') && currentTime >= checkoutTime;
}

function useCheckoutSettings() {
  const {housing, data: reservation} = useReservation();
  const {data: summary} = useSummary();

  const checkoutSettingsId = housing?.check_out_settings_id;
  const isAvailableOrigin = Boolean(
    reservation?.origin && AVAILABLE_ORIGINS.includes(reservation.origin),
  );
  const shouldFetchSettings = Boolean(checkoutSettingsId) && isAvailableOrigin;

  const {data: checkoutSettings, isLoading} = useFetchCheckoutSettings({
    checkoutSettingsId,
    enabled: shouldFetchSettings,
  });

  const isCheckoutEnabled =
    shouldFetchSettings && Boolean(checkoutSettings?.is_check_out_enabled);

  const hasMozio = summary?.mozio_status === OFFER_STATUSES.active;
  const isCheckoutDate = getIsCheckoutDate(
    reservation?.check_out_date,
    checkoutSettings?.check_out_button_availability_time,
  );
  const isSurveyFlowEnabled = checkoutSettings?.is_survey_enabled;
  const isCheckoutCompleted = isSurveyFlowEnabled
    ? reservation?.check_out_flow_status === 'FEEDBACK_SUBMITTED'
    : reservation?.check_out_flow_status === 'PAID';

  const isAvailable = isCheckoutEnabled && isCheckoutDate;

  return {
    checkoutSettings,
    isAvailable,
    checkoutFlowStatus: reservation?.check_out_flow_status,
    isSurveyFlowEnabled,
    isCheckoutCompleted: !isLoading && isCheckoutCompleted,
    isLoading,
    isCheckoutEnabled,
    hasMozio,
    isCheckoutDate,
    checkoutDate: reservation?.check_out_date,
    availabilityTime: checkoutSettings?.check_out_button_availability_time,
  };
}

export {useCheckoutSettings};
